/* Suggestions.tsx generated by @compiled/babel-plugin v0.36.1 */
/* eslint-disable @repo/internal/dom-events/no-unsafe-event-listeners */
/** @jsxfrag */
import "./Suggestions.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
import React, { useMemo } from 'react';
import debounce from 'lodash/debounce';
import kebabCase from 'lodash/kebabCase';
import { useIntl } from 'react-intl-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { ListHeading } from './ListHeading';
import { ListItem } from './ListItem';
import { messages } from './messages';
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var suggestionsContainerStylesUnbounded = null;
var suggestionsContainerStyles = null;

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var suggestionsHeadingStyles = null;

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var suggestionsGroupStyles = null;
var suggestionsGroupWrapStyles = null;
var suggestionsScrollerPostGAStyles = {
  default: "_16jlidpf _1o9zkb7n _i0dl1wug _18m91wug"
};
var suggestionsContainerTeam25Styles = null;
export function reduceSuggestionsIntoGroups(output, suggestion) {
  var suggestionGroup;
  if (typeof suggestion.groupHeading === 'function') {
    suggestionGroup = suggestion.groupHeading();
  } else {
    suggestionGroup = suggestion.groupHeading || '';
  }
  var existingRecordIndex = output.findIndex(function (s) {
    return s.groupHeading === suggestionGroup;
  });

  // If the group doesn't exist, create it
  if (existingRecordIndex === -1) {
    var suggestionsLength = output.push({
      groupHeading: suggestionGroup,
      suggestions: []
    });
    existingRecordIndex = suggestionsLength - 1;
  }
  output[existingRecordIndex].suggestions.push(suggestion);
  return output;
}
export var Suggestions = function Suggestions(_ref) {
  var suggestionsHeading = _ref.suggestionsHeading,
    suggestions = _ref.suggestions,
    value = _ref.value,
    setSuggestion = _ref.setSuggestion,
    hideGroupHeading = _ref.hideGroupHeading;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;

  /**
   * While scrolling with the mouse, the setSuggestion() function can still be
   * called by the mousemove event in ListItem.
   *
   * So while the scrolling is happening, another scrollTo() is started and
   * conflicts with the current scrolling momentum, causing the experience to
   * jitter and feel clunky.
   *
   * While scrolling with just the mouse, this is the order of events:
   * - (multiple) scroll
   * - mousemove
   * - (multiple) scroll
   * - mousemove
   * - scrollend
   *
   * However, if the user leaves the cursor over the list while navigating with
   * the keyboard and it scrolls to ensure items are visible, the order of
   * events is:
   * - (multiple) on scroll
   * - scrollend
   * - mousemove
   *
   * So to avoid the mousemove from triggering a change, we set a flag to
   * ignore mousemove events until a bit after its over.
   */
  var ignoreMouseMoveRef = React.useRef(false);
  var scrollerRef = React.useRef(null);
  var smoothSetSuggestion = React.useCallback(function (suggestion, eventName) {
    if (!setSuggestion) {
      return;
    }

    // Ignore mousemove
    if (eventName === 'mousemove' && ignoreMouseMoveRef.current) {
      return;
    }
    setSuggestion(suggestion);
  }, [setSuggestion]);
  React.useEffect(function () {
    if (!scrollerRef.current) {
      return;
    }

    // Debounced handler to restore tracking after scrolling
    var allowMouseTracking = debounce(function () {
      ignoreMouseMoveRef.current = false;
    }, 75);
    var onScrollEvent = function onScrollEvent() {
      ignoreMouseMoveRef.current = true;
      allowMouseTracking();
    };
    var scroller = scrollerRef.current;
    scroller.addEventListener('scroll', onScrollEvent);
    scroller.addEventListener('scrollend', onScrollEvent);
    return function () {
      scroller.removeEventListener('scroll', onScrollEvent);
      scroller.removeEventListener('scrollend', onScrollEvent);
    };
  }, [scrollerRef]);
  var renderedGroupSuggestions = useMemo(function () {
    var customLabelMap = {
      'tell-ai': formatMessage(messages.tellAILabel),
      'tell-agent': formatMessage(messages.tellAgentLabel)
    };
    var mapSuggestionsCallback = function mapSuggestionsCallback(suggestionGroup) {
      if (fg('editor_a11y_ai_suggestions_list_structure')) {
        var groupHeadingId = "list-heading-".concat(kebabCase(suggestionGroup.groupHeading));
        return /*#__PURE__*/React.createElement(React.Fragment, null, suggestionGroup.groupHeading && !hideGroupHeading && /*#__PURE__*/React.createElement(ListHeading, {
          id: groupHeadingId,
          heading: suggestionGroup.groupHeading
        }), /*#__PURE__*/React.createElement("ul", {
          "aria-labelledby": groupHeadingId,
          "data-testid": "suggestion-group-".concat(kebabCase(suggestionGroup.groupHeading || 'Atlassian intelligence')),
          key: "group_".concat(suggestionGroup.groupHeading),
          className: ax(["_19pkidpf _2hwxidpf _otyridpf _18u0idpf _ca0qidpf _u5f3idpf _n3tdidpf _19bvidpf"])
        }, suggestionGroup.suggestions.map(function (suggestion) {
          return /*#__PURE__*/React.createElement(ListItem, {
            key: suggestion.value,
            suggestion: suggestion,
            customLabel: customLabelMap[suggestion.value] || undefined,
            highlighted: suggestion.value === value,
            setSuggestion: smoothSetSuggestion,
            hasGroupHeading: !!suggestionGroup.groupHeading && !hideGroupHeading
          });
        })));
      }
      return /*#__PURE__*/React.createElement("li", {
        "data-testid": "suggestion-group-".concat(kebabCase(suggestionGroup.groupHeading || 'Atlassian intelligence')),
        key: "group_".concat(suggestionGroup.groupHeading),
        className: ax(["_19pkidpf _2hwxidpf _otyridpf _18u0idpf"])
      }, /*#__PURE__*/React.createElement("ul", {
        className: ax(["_19pkidpf _2hwxidpf _otyridpf _18u0idpf _ca0qidpf _u5f3idpf _n3tdidpf _19bvidpf"])
      }, suggestionGroup.groupHeading && !hideGroupHeading && /*#__PURE__*/React.createElement(ListHeading, {
        heading: suggestionGroup.groupHeading
      }), suggestionGroup.suggestions.map(function (suggestion) {
        return /*#__PURE__*/React.createElement(ListItem, {
          key: suggestion.value,
          suggestion: suggestion,
          customLabel: customLabelMap[suggestion.value] || undefined,
          highlighted: suggestion.value === value,
          setSuggestion: smoothSetSuggestion,
          hasGroupHeading: !!suggestionGroup.groupHeading && !hideGroupHeading
        });
      })));
    };
    return (suggestions || []
    // Group suggestions by heading to make mapping to components easier later on
    ).reduce(reduceSuggestionsIntoGroups, []).map(mapSuggestionsCallback);
  }, [formatMessage, smoothSetSuggestion, suggestions, value, hideGroupHeading]);
  if (!renderedGroupSuggestions.length) {
    return null;
  }

  /**
   * When cleaning up editor_a11y_ai_suggestions_list_structure,
   * change to use <div> instead of <DivOrUl>
   */
  var DivOrUl = fg('editor_a11y_ai_suggestions_list_structure') ? 'div' : 'ul';
  return /*#__PURE__*/React.createElement(Box, {
    testId: "suggestions-container",
    xcss: suggestionsScrollerPostGAStyles.default,
    ref: scrollerRef,
    style: {
      borderTop: "1px solid ".concat("var(--ds-border, #091E4224)")
    }
  }, suggestionsHeading && /*#__PURE__*/React.createElement("h2", {
    className: ax(["_fqox1o8v _rcujze3t _o81cze3t _1l6uze3t _149mze3t _1dlt12x7 _w8squtpp _fjwr12x7 _1yxxutpp _1y2cmoej _nmk4angw _1w901gjq"])
  }, suggestionsHeading), /*#__PURE__*/React.createElement(DivOrUl, {
    "data-testid": "editor-plugin-ai-suggestion-list",
    id: "suggestion-list",
    className: ax([fg('ai-smart-button-team-25') && "_qtt8glyw _kqswh2mm _1bsb1osq _4t3i1osq _19bvze3t _u5f3ze3t _ca0qze3t _n3tdutpp", !fg('ai-smart-button-team-25') && "_qtt8glyw _1bsb1osq _4t3i1osq _19pkze3t _2hwxze3t _otyrze3t _18u0ze3t _ca0qze3t _u5f3ze3t _n3tdze3t _19bvze3t _1e0c1o8l _kqswh2mm", !fg('ai-smart-button-team-25') && "_1x7s1kw7 _136z1kw7 _19511kw7 _jb8p1kw7"])
  }, renderedGroupSuggestions));
};